import React from 'react';

import { Android, Apple, PhoneIphone, Room } from '@mui/icons-material';
import { Chip, Link, Stack } from '@mui/material';

import { HighlightableStringView } from '@/app/log-viewer/highlighting/HighlightableStringView';
import { PlatformType } from '@/parsing/Log';

import { LogViewTableRow, LogViewTableRowProps } from '../../LogViewerTableRow';
import { shouldPassOnClick } from '../shouldPassOnClick';
import { SummaryRowModel } from './SummaryRowModel';

interface Props extends LogViewTableRowProps {
  model: SummaryRowModel;
}

export class LogViewerTableSummaryRow extends LogViewTableRow<Props> {
  render() {
    const summary = this.props.model.data.summary;
    const platformType = summary.deviceInfo.platformType;

    return (
      <div
        className={`logViewerTableRow logViewerTableSummaryRow ${
          this.props.model.data.isSelected ? 'logViewerTableRowSelected' : ''
        }`}
        onClick={(e) => {
          if (shouldPassOnClick(e.nativeEvent.composedPath())) {
            this.props.onClick();
          }
        }}
      >
        <b>
          <HighlightableStringView string={summary.title} />
        </b>

        <Stack direction="column" spacing="8px" sx={{ marginTop: '8px' }}>
          {(summary.deviceInfo.deviceType ||
            summary.deviceInfo.deviceOsVersion ||
            summary.deviceInfo.deviceName ||
            summary.deviceInfo.appVersion) && (
            <Stack direction="row" spacing="8px" flexWrap="wrap" alignItems="center">
              {summary.deviceInfo.deviceType && (
                <Chip
                  size="small"
                  icon={platformType === PlatformType.Android ? <Android /> : <Apple />}
                  label={<HighlightableStringView string={summary.deviceInfo.deviceType} />}
                  sx={{
                    backgroundColor: platformType === PlatformType.Android ? '#A4C639' : '#555',
                    color: '#fff',
                    fontWeight: 500,
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
              )}
              {summary.deviceInfo.deviceOsVersion && (
                <Chip
                  size="small"
                  label={
                    <span>
                      <span style={{ opacity: 0.7 }}>OS </span>
                      <HighlightableStringView string={summary.deviceInfo.deviceOsVersion} />
                    </span>
                  }
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              )}
              {summary.deviceInfo.deviceName && (
                <Chip
                  size="small"
                  icon={<PhoneIphone sx={{ fontSize: 16 }} />}
                  label={<HighlightableStringView string={summary.deviceInfo.deviceName} />}
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              )}
              {summary.deviceInfo.appVersion && (
                <Chip
                  size="small"
                  label={<HighlightableStringView string={summary.deviceInfo.appVersion} />}
                  sx={{
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    fontWeight: 500,
                  }}
                />
              )}
            </Stack>
          )}

          {summary.foundUserIDs.length > 0 ? (
            <div>
              <div>
                <HighlightableStringView string={summary.foundUserIDsTitle} />
              </div>
              <ul className="summaryUL">
                {summary.foundUserIDs.map((id, index) => {
                  return (
                    <li key={index}>
                      <Stack direction="row" spacing="12px">
                        <HighlightableStringView string={id.userId} />
                        {id.mixpanelLink && (
                          <Link target="_blank" href={id.mixpanelLink}>
                            <HighlightableStringView string={id.mixpanelLinkTitle} />
                          </Link>
                        )}
                        <Link target="_blank" href={id.adminPanelLink}>
                          <HighlightableStringView string={id.adminPanelLinkTitle} />
                        </Link>
                      </Stack>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {summary.foundOrders.length > 0 ? (
            <div>
              <HighlightableStringView string={summary.foundOrdersTitle} />
              <ul className="summaryUL">
                {summary.foundOrders.map((foundOrder, index) => {
                  return (
                    <li key={index}>
                      <Stack direction="column" spacing="2px">
                        <Stack direction="row" spacing="12px">
                          <HighlightableStringView string={foundOrder.orderId} />
                          <Link target="_blank" href={foundOrder.adminPanelLink}>
                            <HighlightableStringView string={foundOrder.adminPanelLinkTitle} />
                          </Link>
                          <Link target="_blank" href={foundOrder.commsHistoryLink}>
                            Comms history
                          </Link>
                        </Stack>

                        <Stack direction="row" spacing="2px" alignItems="center">
                          <Room sx={{ color: '#0a0', transform: 'scale(0.75)' }} />
                          <HighlightableStringView string={foundOrder.pickup} />
                        </Stack>

                        {foundOrder.destinations.map((destination, index) => (
                          <Stack key={index} direction="row" spacing="2px" alignItems="center">
                            <Room sx={{ color: '#a606db', transform: 'scale(0.75)' }} />
                            <HighlightableStringView string={destination} />
                          </Stack>
                        ))}
                      </Stack>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </Stack>

        <div className="lineBetweenRows" />
      </div>
    );
  }
}
